import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, MapPin, Ticket as TicketIcon, Minus, Plus, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import ReviewList from '@/components/ReviewList';
import ReviewForm from '@/components/ReviewForm';
import ContactOrganizerForm from '@/components/ContactOrganizerForm';
import ShareEventButton from '@/components/ShareEventButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventPublic() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [zones, setZones] = useState([]);
  const [ticketTypeZones, setTicketTypeZones] = useState({});
  const [cart, setCart] = useState({});
  const [buyerInfo, setBuyerInfo] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, ticketTypesRes, zonesRes] = await Promise.all([
        axios.get(`${API_URL}/events/${eventId}`),
        axios.get(`${API_URL}/events/${eventId}/ticket-types`),
        axios.get(`${API_URL}/events/${eventId}/access-zones`)
      ]);
      
      setEvent(eventRes.data);
      setTicketTypes(ticketTypesRes.data);
      setZones(zonesRes.data);

      // Fetch zones for each ticket type
      const zonesMap = {};
      for (const tt of ticketTypesRes.data) {
        const zonesRes = await axios.get(`${API_URL}/ticket-types/${tt.id}/zones`);
        zonesMap[tt.id] = zonesRes.data;
      }
      setTicketTypeZones(zonesMap);
    } catch (error) {
      toast.error('Errore nel caricamento evento');
    } finally {
      setLoading(false);
    }
  };

  const updateCart = (ticketTypeId, delta) => {
    setCart(prev => {
      const current = prev[ticketTypeId] || 0;
      const newValue = Math.max(0, current + delta);
      if (newValue === 0) {
        const { [ticketTypeId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketTypeId]: newValue };
    });
  };

  const getTotalPrice = () => {
    return Object.entries(cart).reduce((sum, [ttId, quantity]) => {
      const ticketType = ticketTypes.find(tt => tt.id === ttId);
      return sum + (ticketType?.price || 0) * quantity;
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (getTotalTickets() === 0) {
      toast.error('Seleziona almeno un biglietto');
      return;
    }

    setPurchasing(true);
    try {
      const orderData = {
        event_id: eventId,
        buyer_name: buyerInfo.buyer_name,
        buyer_email: buyerInfo.buyer_email,
        buyer_phone: buyerInfo.buyer_phone,
        tickets: Object.entries(cart).map(([ticket_type_id, quantity]) => ({
          ticket_type_id,
          quantity
        }))
      };

      const response = await axios.post(`${API_URL}/orders`, orderData);
      toast.success('Ordine completato!');
      navigate(`/order/${response.data.id}/confirmation`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'acquisto');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-lg text-gray-600">Caricamento...</div>
      </div>
    );
  }

  if (!event || event.status !== 'published') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Evento non disponibile</h2>
            <p className="text-gray-600">Questo evento non è al momento accessibile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{event.title} | ZonePass</title>
        <meta name="title" content={`${event.title} | ZonePass`} />
        <meta name="description" content={event.description || `Partecipa a ${event.title} - ${format(new Date(event.start_date), 'dd MMMM yyyy', { locale: it })}`} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="event" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="ZonePass - Gestione Eventi" />
        <meta property="og:title" content={`🎉 ${event.title}`} />
        <meta property="og:description" content={`📅 ${format(new Date(event.start_date), 'dd MMMM yyyy, HH:mm', { locale: it })}\n📍 ${event.location}\n\n${event.description || 'Acquista i tuoi biglietti su ZonePass!'}`} />
        <meta property="og:image" content={event.cover_image_url || `${window.location.origin}/zonepass-logo-final.png`} />
        <meta property="og:image:secure_url" content={event.cover_image_url || `${window.location.origin}/zonepass-logo-final.png`} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${event.title} - ZonePass`} />
        <meta property="og:locale" content="it_IT" />
        
        {/* Event specific OG tags */}
        <meta property="event:start_time" content={new Date(event.start_date).toISOString()} />
        <meta property="event:end_time" content={new Date(event.end_date).toISOString()} />
        <meta property="place:location:latitude" content={event.latitude || ""} />
        <meta property="place:location:longitude" content={event.longitude || ""} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ZonePass" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`🎉 ${event.title}`} />
        <meta name="twitter:description" content={`📅 ${format(new Date(event.start_date), 'dd MMMM yyyy, HH:mm', { locale: it })} | 📍 ${event.location}`} />
        <meta name="twitter:image" content={event.cover_image_url || `${window.location.origin}/zonepass-logo-final.png`} />
        <meta name="twitter:image:alt" content={`${event.title} - ZonePass`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 py-12 px-4" data-testid="event-public-page">
      <div className="max-w-4xl mx-auto">
        {/* Event Header */}
        <Card className="mb-8 overflow-hidden">
          {event.cover_image_url && (
            <img
              src={event.cover_image_url}
              alt={event.title}
              className="w-full h-64 object-cover"
            />
          )}
          <CardContent className="p-8">
            <div className="flex items-start justify-between mb-4 gap-3">
              <h1 className="text-4xl font-bold text-gray-900 flex-1">{event.title}</h1>
              <div className="flex gap-2">
                <Button 
                  variant="default"
                  size="default"
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white gap-2"
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/events/${event.id}/public/share`;
                    const message = `🎉 *${event.title}*\n\n📅 ${format(new Date(event.start_date), 'dd MMMM yyyy, HH:mm', { locale: it })}\n📍 ${event.location}\n\n🎟️ Vedi evento:\n${shareUrl}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Condividi su WhatsApp
                </Button>
                <ShareEventButton event={event} variant="outline" />
              </div>
            </div>
            <p className="text-gray-600 text-lg mb-6">{event.description}</p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center text-gray-700">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                <span className="font-medium">
                  {format(new Date(event.start_date), 'dd MMMM yyyy, HH:mm', { locale: it })}
                </span>
              </div>
              <div className="flex items-center text-gray-700">
                <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                <span className="font-medium">{event.location}</span>
              </div>
              
              {/* Event Type Badge */}
              <div className="flex items-center">
                {event.event_type === 'online' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    🌐 Online
                  </span>
                )}
                {event.event_type === 'hybrid' && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    🔄 Ibrido (Dal Vivo + Online)
                  </span>
                )}
                {event.event_type === 'in_person' && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    📍 Dal Vivo
                  </span>
                )}
              </div>
            </div>
            
            {/* Streaming Link for Online/Hybrid Events */}
            {(event.event_type === 'online' || event.event_type === 'hybrid') && (
              <div className="mt-6">
                {event.use_integrated_streaming && event.jitsi_room_name ? (
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🎥</span>
                      </div>
                      <div>
                        <p className="font-bold text-purple-900">Streaming Integrato ZonePass</p>
                        <p className="text-sm text-purple-700">Videoconferenza inclusa nel biglietto</p>
                      </div>
                    </div>
                    <a
                      href={`/events/${event.id}/stream`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-full text-center bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    >
                      🚀 Entra nella Diretta
                    </a>
                    <p className="text-xs text-purple-600 mt-2 text-center">
                      Link disponibile anche nel tuo biglietto
                    </p>
                  </div>
                ) : event.streaming_url ? (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 mb-2 font-medium">📺 Link Diretta Streaming:</p>
                    <a 
                      href={event.streaming_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline break-all font-semibold"
                    >
                      {event.streaming_url}
                    </a>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket Selection */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Seleziona Biglietti</h2>
            <div className="space-y-4">
              {ticketTypes.map(ticketType => {
                const available = ticketType.quantity_available - ticketType.quantity_sold;
                const ticketZones = ticketTypeZones[ticketType.id] || [];
                
                return (
                  <div
                    key={ticketType.id}
                    className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400"
                    data-testid={`ticket-type-${ticketType.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{ticketType.name}</h3>
                        <p className="text-gray-600 mb-3">{ticketType.description}</p>
                        
                        {ticketZones.length > 0 && (
                          <div className="flex items-start text-sm text-gray-700 bg-blue-50 p-3 rounded-lg">
                            <Shield className="w-4 h-4 mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                            <div>
                              <span className="font-medium">Accesso a: </span>
                              {ticketZones.map(z => z.name).join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-6">
                        <div className="text-3xl font-bold text-blue-600">€{ticketType.price.toFixed(2)}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {available > 0 ? `${available} disponibili` : 'Esauriti'}
                        </div>
                      </div>
                    </div>
                    
                    {available > 0 && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <span className="text-gray-700 font-medium">Quantità:</span>
                        <div className="flex items-center gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCart(ticketType.id, -1)}
                            disabled={!cart[ticketType.id]}
                            data-testid={`decrease-${ticketType.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-lg font-semibold w-8 text-center" data-testid={`quantity-${ticketType.id}`}>
                            {cart[ticketType.id] || 0}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCart(ticketType.id, 1)}
                            disabled={(cart[ticketType.id] || 0) >= available}
                            data-testid={`increase-${ticketType.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Buyer Info & Checkout */}
        {getTotalTickets() > 0 && (
          <Card>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Informazioni Acquirente</h2>
              <form onSubmit={handlePurchase} className="space-y-4">
                <div>
                  <Label htmlFor="buyer_name">Nome Completo *</Label>
                  <Input
                    id="buyer_name"
                    value={buyerInfo.buyer_name}
                    onChange={(e) => setBuyerInfo({...buyerInfo, buyer_name: e.target.value})}
                    required
                    data-testid="buyer-name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="buyer_email">Email *</Label>
                  <Input
                    id="buyer_email"
                    type="email"
                    value={buyerInfo.buyer_email}
                    onChange={(e) => setBuyerInfo({...buyerInfo, buyer_email: e.target.value})}
                    required
                    data-testid="buyer-email-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="buyer_phone">Telefono *</Label>
                  <Input
                    id="buyer_phone"
                    type="tel"
                    value={buyerInfo.buyer_phone}
                    onChange={(e) => setBuyerInfo({...buyerInfo, buyer_phone: e.target.value})}
                    required
                    data-testid="buyer-phone-input"
                    className="mt-1"
                  />
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <div className="text-gray-600">Totale Biglietti</div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="total-tickets">{getTotalTickets()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-600">Totale</div>
                      <div className="text-3xl font-bold text-blue-600" data-testid="total-price">
                        €{getTotalPrice().toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={purchasing}
                    data-testid="purchase-button"
                  >
                    <TicketIcon className="w-5 h-5 mr-2" />
                    {purchasing ? 'Elaborazione...' : 'Acquista Biglietti'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Contact Organizer Section */}
        {event && (
          <div className="mt-12">
            <ContactOrganizerForm eventId={eventId} eventName={event.title} />
          </div>
        )}

        {/* Event Reviews Section */}
        {event && (
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Recensioni dell'Evento</h2>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Show Reviews */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Cosa dicono i partecipanti</h3>
                <ReviewList eventId={eventId} limit={10} />
              </div>

              {/* Leave Review */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Lascia una recensione</h3>
                <ReviewForm eventId={eventId} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
